import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock, Code2, AlertCircle, TableProperties } from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { UserManagementView } from '../../auth/components/UserManagementView.js';
import { DatabaseBackupView } from './DatabaseBackupView.js';
import { NewsletterManagementView } from './NewsletterManagementView.js';
import { AdminSettingsView } from './AdminSettingsView.js';
import { BlogManager } from '../../blog/index.js';
import {
  AdminTabHeader,
  AdminTab,
  ApiDocsBanner,
  DatabaseSchemaTab,
  ApiEndpointsTab,
} from './api-docs/index.js';

export const ApiDocsView: React.FC = () => {
  const { user, token } = useAuthStore();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [docsSubTab, setDocsSubTab] = useState<'endpoints' | 'schema'>('endpoints');

  const { data: docs, isLoading, error } = useQuery({
    queryKey: ['apiDocs'],
    queryFn: () => api.getApiDocs(),
    enabled: user?.role === 'admin',
  });

  if (user?.role !== 'admin') {
    return (
      <div className="p-12 text-center bg-white rounded-2xl border border-rose-200">
        <Lock className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <h2 className="text-base font-bold text-slate-900">Akses Terbatas: Admin Panel</h2>
        <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
          Dokumentasi API sistem UIBN ini dilindungi dan hanya dapat diakses oleh akun dengan peran Administrator.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-12 text-center bg-white rounded-2xl border border-slate-200">
        <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-xs text-slate-500">Memuat spesifikasi dokumentasi API...</p>
      </div>
    );
  }

  if (error || !docs) {
    return (
      <div className="p-8 text-center bg-white rounded-2xl border border-slate-200">
        <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
        <p className="text-sm font-semibold text-slate-800">Gagal memuat dokumentasi API</p>
        <p className="text-xs text-slate-500 mt-1">Pastikan Anda memiliki hak akses Administrator yang sah.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title & Tab Navigation */}
      <AdminTabHeader activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'users' ? (
        <UserManagementView />
      ) : activeTab === 'blog' ? (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <BlogManager />
        </div>
      ) : activeTab === 'database' ? (
        <DatabaseBackupView />
      ) : activeTab === 'newsletter' ? (
        <NewsletterManagementView />
      ) : activeTab === 'settings' ? (
        <AdminSettingsView />
      ) : (
        <div className="space-y-6">
          {/* Hono OpenAPI 3.0 & Swagger UI Integration Banner */}
          <ApiDocsBanner />

          {/* Sub-tab Navigation for Docs (Endpoints vs Database Schema Diagram) */}
          <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
            <button
              onClick={() => setDocsSubTab('endpoints')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
                docsSubTab === 'endpoints'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Code2 className="w-4 h-4" />
              <span>Endpoints RESTful API</span>
            </button>

            <button
              onClick={() => setDocsSubTab('schema')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
                docsSubTab === 'schema'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <TableProperties className="w-4 h-4" />
              <span>Diagram Skema Database (Mermaid.js)</span>
            </button>
          </div>

          {docsSubTab === 'schema' ? (
            <DatabaseSchemaTab />
          ) : (
            <ApiEndpointsTab docs={docs} token={token} />
          )}
        </div>
      )}
    </div>
  );
};
